import { DispatchManifestStopDetailStatus } from "app/data/city-delivery/dispatch-board/models";
import { PickupStatus } from "app/data/common/route";
import type { Pickup } from "slices/pickup/UnassignedPickupState";
import type { FreightItemForDisplay } from "../../common/pickup-details/pickupFreightItemsBlock";

/** Single pickup row (API-mapped) used in unassigned lists. */
export type UnassignedPickup = {
  id: string;
  title: string;
  address: string;
  timeRange: string;
  type: "Delivery" | "Pickup";
  puNumber: string;
  consigneeName: string;
  pieces: number;
  weight: string;
  eta: string;
  shipperName: string;
  date: string;
  status: string[];
  probillStatus: string | null;
  zip: string;
  probillNumber: number | null;
  /** Raw schedule fields for At Risk / Late map borders */
  pickupDateForTiming: string;
  readyTimeForTiming: string;
  closeTimeForTiming: string;
  rollsUnits: number;
  palletsUnits: number;
  totalItemsWeight: number;
  pickupWindowLine: string;
  shipperContactLine: string;
  consigneeAddressLine: string;
  consigneeContactLine: string;
  itemLines: FreightItemForDisplay[];
};

/** Grouped stop: same shipper name, address, and pickup window within a static route. */
export type UnassignedStopGroup = {
  id: string;
  pickups: UnassignedPickup[];
  groupStopStatus: DispatchManifestStopDetailStatus;
  /** Shipper coordinates for map (first pickup in group). */
  lat?: number;
  lng?: number;
};

export type UnassignedRoute = {
  id: string;
  name: string;
  count: number;
  stops: UnassignedStopGroup[];
};

export function aggregateUnassignedGroupStatus(
  pickups: UnassignedPickup[]
): DispatchManifestStopDetailStatus {
  const completed = pickups.filter((p) => p.status[0] === PickupStatus.COMPLETED).length;
  const total = pickups.length;
  if (total === 0) return DispatchManifestStopDetailStatus.PENDING;
  if (completed === total) return DispatchManifestStopDetailStatus.COMPLETED;
  if (completed > 0) return DispatchManifestStopDetailStatus.IN_PROGRESS;
  return DispatchManifestStopDetailStatus.PENDING;
}

/** Group key: shipper name + address + pickup ready/close times. */
export function staticRoutePickupGroupKey(p: Pickup): string {
  const addr = `${p.shipperAddress1}|${p.shipperCity}|${p.shipperState}|${p.shipperPostalCode}`;
  return `${(p.shipperName ?? "").trim()}|${addr}|${p.pickupReadyTime}|${p.pickupCloseTime}`;
}
